import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { HopChartsProvider } from 'hopcharts';
import { marketProvider, queryClient } from './lib/market';
import { applyTheme, useSettings } from './state/settings';
import { Toaster } from './components/ui/Toast';
import { useAlertEngine } from './features/alerts/alertsStore';
import { RouteError } from './pages/RouteError';
import { basename } from './lib/i18n';
import { useLanguageAlternates } from './lib/meta';

const LandingPage = lazy(() => import('./pages/LandingPage').then((m) => ({ default: m.LandingPage })));
const TerminalPage = lazy(() => import('./features/terminal/TerminalPage').then((m) => ({ default: m.TerminalPage })));
const MarketsPage = lazy(() => import('./pages/MarketsPage').then((m) => ({ default: m.MarketsPage })));
const AgentPage = lazy(() => import('./features/agent/AgentPage').then((m) => ({ default: m.AgentPage })));
const WidgetsPage = lazy(() => import('./features/widgets/WidgetsPage').then((m) => ({ default: m.WidgetsPage })));
const EmbedPage = lazy(() => import('./features/widgets/EmbedPage').then((m) => ({ default: m.EmbedPage })));
const SignalsPage = lazy(() => import('./features/signals/SignalsPage').then((m) => ({ default: m.SignalsPage })));
const CalculatorsPage = lazy(() => import('./features/calculators/CalculatorsPage').then((m) => ({ default: m.CalculatorsPage })));
const ConverterPage = lazy(() => import('./features/converter/ConverterPage').then((m) => ({ default: m.ConverterPage })));
const SupportPage = lazy(() => import('./pages/SupportPage').then((m) => ({ default: m.SupportPage })));
const GlossaryPage = lazy(() => import('./pages/GlossaryPage').then((m) => ({ default: m.GlossaryPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));

function PageFallback() {
  return <div className="h-dvh bg-ink" aria-busy="true" />;
}

/** Shared providers for the site: market data, query cache, theme, alerts, toasts. */
function Root() {
  const theme = useSettings((s) => s.theme);
  useEffect(() => applyTheme(theme), [theme]);
  useAlertEngine();
  useLanguageAlternates();
  return (
    <>
      <Suspense fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
      <Toaster />
    </>
  );
}

/** Widgets in iframes: no alert engine, theme comes from the URL. */
function EmbedRoot() {
  return (
    <Suspense fallback={<div className="h-dvh" aria-busy="true" />}>
      <Outlet />
    </Suspense>
  );
}

// Created on first render, after main.tsx has settled the locale that sets the basename.
let router: ReturnType<typeof createBrowserRouter> | undefined;
const getRouter = () => (router ??= createBrowserRouter([
  {
    path: '/embed',
    element: <EmbedRoot />,
    errorElement: <RouteError />,
    children: [{ path: ':widget', element: <EmbedPage /> }],
  },
  {
    element: <Root />,
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <LandingPage /> },
      { path: '/chart', element: <TerminalPage /> },
      { path: '/chart/:symbol', element: <TerminalPage /> },
      { path: '/markets/:tab?', element: <MarketsPage /> },
      { path: '/signals', element: <SignalsPage /> },
      { path: '/ai', element: <AgentPage /> },
      { path: '/widgets', element: <WidgetsPage /> },
      { path: '/calculators', element: <CalculatorsPage /> },
      { path: '/converter', element: <ConverterPage /> },
      { path: '/support', element: <SupportPage /> },
      { path: '/glossary', element: <GlossaryPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
], { basename: basename() }));

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HopChartsProvider provider={marketProvider} queryClient={queryClient}>
        <RouterProvider router={getRouter()} />
      </HopChartsProvider>
    </QueryClientProvider>
  );
}
